package com.dynamicruntime.webapp

import com.dynamicruntime.common.util.toJsonListOfMaps
import com.dynamicruntime.common.home.HFRAG
import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.gedra.GDF
import com.dynamicruntime.common.gedra.ListingColumn
import com.dynamicruntime.common.gedra.GE
import com.dynamicruntime.common.gedra.UF
import com.dynamicruntime.common.gedra.GSORT
import com.dynamicruntime.common.gedra.workflow.WAGG
import com.dynamicruntime.common.home.HMENU
import com.dynamicruntime.common.schema.SchLayout
import com.dynamicruntime.common.schema.SchType
import com.dynamicruntime.common.util.toJsonListOrEmpty
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toOptLong
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useEffectOnce
import react.useRef
import react.useState
import web.cssom.ClassName

/** The sort request params (issue #666): the column and direction, or nothing for the default order. */
private fun sortArgs(col: String?, descending: Boolean): Map<String, Any?> =
    if (col == null) emptyMap() else mapOf(GSORT.sort to col, GSORT.sortDir to if (descending) GSORT.desc else GSORT.asc)

/** The sort as route params (issue #666): shareable and restored on mount, like the applied search. */
private fun sortRouteParams(col: String?, descending: Boolean): List<Pair<String, String>> =
    if (col == null) emptyList() else listOf(GSORT.sort to col, GSORT.sortDir to if (descending) GSORT.desc else GSORT.asc)

/** Coroutine scope for the forms page's suspend calls (the catalog fetch, the list page, and a single fetch). */
private val formsScope = MainScope()

/** What identifies a forms-page destination: which form is open. Everything else refines it. */
private val formsIdentity = setOf(HP.gedra)

/**
 * The **workflow's listing** route (issue #792): the forms in one state -- eligible, engaged, finished -- for one
 * normal workflow, where a count on the Workflows page leads. Its own page under Workflows rather than My forms
 * filtered: the workflow and the state are what the page *is* (its heading, a state switch, a way back to
 * Workflows), not a chip among the trait filters that Clear would drop and a stranger to the page would miss.
 * Drawn by [FormsPage] in workflow mode, so the rows, the search within them and every row action are the
 * listing's own. Frontend-only, like the editor routes: reached from the Workflows page, not the server-built nav.
 */
const val pageWorkflowForms = "workflowForms"

/** The forms page's one prop: which listing it is drawing. */
external interface FormsPageProps : Props {
    /** [pageWorkflowForms] for a workflow's listing; absent (or [HMENU.pageForms]) for My forms. */
    var listing: String?
}

/** How long the just-saved row keeps its flash before the highlight is cleared (issue #592). */
private const val formsHighlightMs = 2500

/** The browser's `setTimeout`/`clearTimeout`, declared locally rather than reaching for a DOM wrapper. */
fun setFormsTimer(block: () -> Unit, delayMs: Int): Int = js("setTimeout(block, delayMs)") as Int
fun clearFormsTimer(id: Int) {
    js("clearTimeout(id)")
}

/**
 * The owner-block request argument (issue #591): asks the listing to attach each document's owner only when the
 * caller draws the User column ([on]), so the backend does the owner lookup exactly where it is used. Empty
 * otherwise, so the parameter is simply absent and the listing carries no owners.
 */
private fun includeUsersArg(on: Boolean): Map<String, Any?> = if (on) mapOf(EI.includeUsers to true) else emptyMap()

/**
 * The read side of the form documents (issue #408): a paged list of the caller's forms, a read-only view of
 * one, and a delete from that view (slice 3). All are driven by the same catalog the create page uses -- the
 * client-scoped `formDocs` list, `formDoc` fetch, and `formDoc` DELETE endpoints -- so what is shown and what
 * can be deleted is exactly what that caller may reach, narrowed by the endpoints' own scope rules rather than
 * by anything decided here.
 *
 * The list is a growing listing (issue #1315, `ShowMore.kt`): the first [listingBatch] forms, then more on request up to
 * [listingCap], against the total the scope admits -- re-queried with the larger limit, so the rows on screen are one
 * snapshot, and with the count shown riding the address (`shown`), so the ways home from a form ask for the same rows. It is ordered
 * most recently written first, and a caller who reads other users' forms -- an administrator, or an editor (issue
 * #1432) -- also gets a scope bar (whose forms) and a User column (issue #562); both key on that (`seesOthersForms`),
 * the backend's predicate for attaching owners, so the controls appear exactly when the rows carry an owner to show. The open form
 * lives in the URL (`g=<id>`), like the Users page's open record; a form the loaded page does not hold --
 * a bookmark, or a link to one now past the first page -- is fetched by id, so a deep link always resolves.
 * The stored row renders through the shared [SchemaForm] in friendly, read-only mode, so its derived system
 * fields drop away and the trait data reads as a filled-in form.
 */
val FormsPage = FC<FormsPageProps> { props ->
    // Which listing this is (issue #792): My forms, or a workflow's listing of forms. The page id every address it
    // writes and every child it opens carries, so a form worked on from a workflow's listing returns to it.
    val listingPage = props.listing ?: HMENU.pageForms
    val workflowMode = listingPage == pageWorkflowForms
    // The workflow's listing pushes a history entry when its workflow or state changes: a move between destinations,
    // as a switch of state is, where on My forms a search change only refines the one destination.
    val identity = if (workflowMode) formsIdentity + formsDrillKeys else formsIdentity
    var catalog by useState<Catalog?>(null)
    var listEndpoint by useState<EndpointInfo?>(null)
    var getEndpoint by useState<EndpointInfo?>(null)
    var deleteEndpoint by useState<EndpointInfo?>(null)
    // The create endpoint (issue #417): its presence is what lets the list offer "New form", so the list is the
    // hub for the whole lifecycle rather than only its read side.
    var createEndpoint by useState<EndpointInfo?>(null)
    // The patch endpoint (issue #417): its presence is what lets the view and each list row offer Edit, on the
    // same "do not show a control that cannot work" rule the delete button follows.
    var patchEndpoint by useState<EndpointInfo?>(null)
    // The field-value suggestions endpoint (issue #581): its presence turns a text filter box into a
    // type-ahead; absent (an older node), the boxes stay plain text.
    var valuesEndpoint by useState<EndpointInfo?>(null)
    var rows by useState<List<Map<String, Any?>>>(emptyList())
    // How many forms the scope admits in all, for "showing X of N" and to know whether there are more to show.
    var numAvailable by useState(0)
    // The workflow column's summary (issue #791), delivered with each page: empty means no column.
    var listingWorkflows by useState<List<WorkflowSummaryEntry>>(emptyList())
    // The listing's columns (issue #1323), from every page's summary: what the table's display columns are, and the
    // presentation each cell -- and an open form's heading -- reads through.
    var listingColumns by useState<List<ListingColumn>>(emptyList())
    // The home file's copy, which the forms pages read (issue #791): what a row with no workflows shows, and the
    // listings' "What is this?" explanations (issue #1337). Empty until read, and if it cannot be.
    var homeCopy by useState(Copy.empty)
    // What a row with no workflows shows (issue #791): the client's copy, Markdown; null draws a dash.
    val emptyWorkflowsCopy = homeCopy.opt(HFRAG.formsNs, HFRAG.noWorkflows)?.ifBlank { null }
    // How many forms the listing asks for (issue #1315): the first batch, grown on request, carried in the address.
    var shown by useState(listingBatch)
    // The forms-list search (issue #538): `draft` is what the boxes hold, `applied` is what the list is
    // currently filtered by. They part so typing does not re-query on every keystroke -- Search promotes the
    // draft, and paging/reloads carry the applied set so a filtered list stays filtered across pages.
    var searchDraft by useState<Map<String, String>>(emptyMap())
    var appliedSearch by useState<Map<String, String>>(emptyMap())
    // What the shell knows of the caller (issue #1091): the page's heading is named by it.
    val shell = useShellFacts()
    // The user the rows on screen are confined to (issues #1081, #1095), as the listing that returned them said --
    // with the scope that listing was asked about, so it is named only while that is still the scope applied.
    var scopeAnswer by useState<ScopeAnswer?>(null)
    // The chosen sort (issue #666): a display trait id or `updated`/`created`, null for the default order, and a
    // direction. Carried in the address like the applied search, and passed to `loadPage` so paging and a new
    // search keep it. The backend ignores an unknown column, so a stale sort in a bookmark still lists.
    var sortColumn by useState<String?>(null)
    var sortDescending by useState(false)
    // Whether the caller reads other users' forms -- an administrator (issue #562) or an editor (issue #1432) -- from the
    // shell's UI-config. False until it answers and false when it cannot, so those controls are never drawn on a guess.
    var seesOthersForms by useState(false)
    // Who the caller is, and whether its deletes are limited -- anyone but an administrator (issue #1432): its own forms,
    // and another's only while in no workflow (issue #1452) -- so a Delete is offered only where it would be honored.
    var selfUserId by useState<Long?>(null)
    var limitedDelete by useState(false)
    // Whether the caller administers across clients (issue #668), from the same UI-config. Gates the Client
    // column and the filter-by-client control; false until known and when it cannot be learned.
    var canSeeAllClients by useState(false)
    // Whether the caller's client declares a survey (issue #695), from the same UI-config: gates the survey-status
    // filter. A fact about the client, not the rows, so the control stays while a filter matches nothing.
    var hasSurvey by useState(false)
    // The clients to offer in the filter-by-client control (issue #668), fetched only for a cross-client caller.
    var clientChoices by useState<List<ClientChoice>>(emptyList())
    // The row to flash on arrival (issue #592): the form just saved on the edit page, read from the address once
    // and cleared after a beat so the flash is a one-time flourish, not a state a reload repeats.
    var highlightRowId by useState<String?>(null)
    val highlightTimer = useRef<Int>(null)
    // Substitute feedback when the just-saved row is not on screen (issues #669, #758): a create (or an edit)
    // returns carrying the row's id to flash, but an active filter -- or, for a create, a sort or a full first
    // page -- can leave it off the page, so the flash has nothing to land on. This note is then the only sign the
    // save took. It persists until the next list action clears it (below) -- clearing the filter, the natural next
    // move, both dismisses it and reveals the row. `formsArrivalNote` is the rule.
    var savedOffscreenNote by useState<String?>(null)
    // Whether the grouped filter panel is open; closed by default so the search does not take the screen.
    var filtersOpen by useState(false)
    // A failure of a search or page reload, shown beside the controls so they stay on screen to be corrected --
    // a user reference outside the caller's access, say -- rather than replacing the whole list. It also keeps
    // the empty state away: a reload that failed with nothing on screen is not "no forms".
    var searchError by useState<DisplayError?>(null)
    var listLoading by useState(true)
    // A "Show more" on its way (issue #1315): the rows on screen stay, unlike [listLoading], so the reader keeps
    // their place and the new rows arrive beneath it.
    var growing by useState(false)
    var error by useState<DisplayError?>(null)
    // True once the initial address restore has run; until then the sync effect stays quiet so it cannot overwrite
    // a `g=` we are about to read (the same gate the Users page uses).
    var restored by useState(false)
    // The search an address change asks for (issue #792), read by an effect with fresh state rather than by the address
    // listener, whose closure is the first render's. Null until an address change names this page.
    var routeSearch by useState<Map<String, String>?>(null)

    // The gedra id of the form open in the view, or null in the list.
    var viewingId by useState<String?>(null)
    // The row being viewed -- from the loaded page when it holds it, otherwise fetched by id. Null while there
    // is nothing to show (list mode, or a fetch in flight).
    var viewRow by useState<Map<String, Any?>?>(null)
    var viewLoading by useState(false)
    // Set when the open id names no form the caller may see -- an old link, or one belonging to someone else.
    var viewMissing by useState(false)
    // A real failure opening the form (a server error, or the server unreachable) -- distinct from viewMissing,
    // which is the "not yours to see" case (issue #519 review).
    var viewError by useState<DisplayError?>(null)

    // Delete of the open form: a two-step confirm, then the call and back to the list (issue #408).
    var confirmingDelete by useState(false)
    var deleting by useState(false)
    var deleteError by useState<DisplayError?>(null)

    // Delete from the list itself (issue #417): the row whose inline confirm is armed, the one whose delete is
    // in flight, and any error from it. Per-id rather than a single flag so the confirm belongs to its own row.
    var rowConfirmDeleteId by useState<String?>(null)
    var rowDeletingId by useState<String?>(null)
    var rowDeleteError by useState<DisplayError?>(null)

    /**
     * Invokes [ep] for one page and returns its rows and total -- the shared body of [loadPage] and
     * [loadForClient], so the standard query args (paging, the filled search, sort, the owner-attach flag, and
     * the survey states) and the items/`numAvailable` parse live in one place and the initial load cannot drift
     * from a paged one. Returned rather than published, so a caller that rebinds the surface can publish the
     * rows *with* it (#714 review). [includeUsers] is passed rather than read from state so a mount-time call is
     * not at the mercy of a not-yet-landed setter (the #668 trap); its caller decides where a failure is shown.
     */
    suspend fun fetchListPage(
        ep: EndpointInfo,
        limit: Int,
        search: Map<String, Any?>,
        sortCol: String?,
        sortDesc: Boolean,
        includeUsers: Boolean,
        withSummary: Boolean = false,
    ): FormsListPage {
        val resp = SchemaCatalogApi.invoke(
            ep,
            // Always from the top (issue #1315): growing the listing asks again for more, so what is on screen is one
            // query's answer.
            mapOf(EP.limit to limit, EP.offset to 0) + search +
                sortArgs(sortCol, sortDesc) + includeUsersArg(includeUsers) +
                mapOf(GDF.withStates to true) +
                // The workflow column's summary (issue #791) only when the forms may have changed: it spans every
                // form the caller may see, which showing more, a search or a sort does not change.
                (if (withSummary) mapOf(GDF.withWorkflowSummary to true) else emptyMap()),
        )
        val fetched = resp[EP.items].toJsonListOrEmpty().map { it.toJsonMapOrEmpty() }
        return FormsListPage(
            fetched,
            (resp[EP.numAvailable] as? Number)?.toInt() ?: fetched.size,
            if (withSummary) parseWorkflowSummary(resp[EP.summary]) else null,
            parseScopeUser(resp[EP.summary]),
            ListingColumn.listFrom(resp[EP.summary].toJsonMapOrEmpty()[GDF.columns]),
        )
    }

    /**
     * What every list action drops before it fetches -- paging, a new search, a delete reload, a client switch
     * (issues #417, #592, #669; shared since the #714 review so the switch cannot skip it). A row's armed delete
     * confirm belongs to the page it was armed on, so a primed "Yes" never lingers on a row navigated past; the
     * just-saved flash plays once on arrival and never re-runs when the rows are rebuilt; and the off-screen
     * "you saved it but it isn't shown" note has done its job once the user acts -- most often by clearing the
     * filter, which reveals the row itself.
     */
    fun beginListAction(grow: Boolean = false) {
        rowConfirmDeleteId = null
        rowDeleteError = null
        highlightRowId = null
        savedOffscreenNote = null
        if (grow) growing = true else listLoading = true
    }

    /**
     * Loads the first [limit] forms from [ep], replacing the rows and the total. [search] carries the client's filled
     * search parameters (issue #538), sent beside the limit; it is passed explicitly rather than read from state so a
     * reload never races a just-applied filter. Flips [listLoading] off when done.
     */
    fun loadPage(
        ep: EndpointInfo,
        limit: Int,
        search: Map<String, Any?>,
        sortCol: String? = sortColumn,
        sortDesc: Boolean = sortDescending,
        /** Ask for the workflow column's summary again (issue #791) -- after a delete, which may change it. */
        withSummary: Boolean = false,
        /**
         * More of the same listing (issue #1315): the rows stay on screen while it loads instead of giving way to
         * "Loading…", which collapsed the page and threw the reader back to the top.
         */
        grow: Boolean = false,
    ) {
        beginListAction(grow)
        formsScope.launch {
            try {
                val page = fetchListPage(ep, limit, search, sortCol, sortDesc, seesOthersForms, withSummary)
                rows = page.rows
                numAvailable = page.numAvailable
                scopeAnswer = ScopeAnswer(search[EI.user] as? String, page.scopeUser)
                page.workflowSummary?.let { listingWorkflows = it }
                listingColumns = page.columns
                searchError = null
            } catch (e: Throwable) {
                searchError = userFacingError(e)
            } finally {
                listLoading = false
                growing = false
            }
        }
    }

    /**
     * Promotes [draft] -- its non-blank values -- to the applied filter and reloads from the top (issue #538).
     * [withSummary] asks for the workflow column's summary again, for a search that changes which workflow the
     * listing is of (issue #792), so the heading's label is looked up in a summary that names it.
     */
    fun applySearch(ep: EndpointInfo, draft: Map<String, String>, withSummary: Boolean = false) {
        val applied = draft.filterValues { it.isNotBlank() }
        appliedSearch = applied
        shown = listingBatch
        loadPage(ep, listingBatch, applied, withSummary = withSummary)
    }

    val appliedUser = appliedSearch[EI.user]?.ifBlank { null }

    // A history move between the workflow's states (issue #792): the state is part of what identifies the page,
    // so a switch pushed an entry, and Back lands on an address whose state differs from the one applied. The page
    // follows it, as it follows `g=` -- only when a drill key moved: a search change writes the address in place
    // and never reaches here as a difference, and an unrelated address change (a form opened) leaves it alone.
    useEffect(routeSearch) {
        val fromRoute = routeSearch ?: return@useEffect
        val ep = listEndpoint ?: return@useEffect
        // Read through the listing's own whitelist, as the mount does: the sort rides the address beside the search
        // and is applied on its own, and a stale key must not reach the endpoint.
        val wanted = fromRoute.filterKeys { it in formsSearchKeys(ep.inputSchema) }
        if (workflowMode && formsDrillKeys.any { wanted[it] != appliedSearch[it] }) {
            searchDraft = wanted
            // Another workflow altogether needs the summary again, or its heading may not be named.
            applySearch(ep, wanted, withSummary = wanted[WAGG.workflowId] != appliedSearch[WAGG.workflowId])
        }
    }

    /**
     * Loads the whole surface for [client] and its first page (issue #714): the catalog for that client (null =
     * the caller's own cross-client surface), which rebinds every form endpoint and -- because a client-scoped
     * `/gedra/<client>/…` path runs bound to that client -- makes the listing's columns, its search fields and
     * its edit form that client's rather than the admin's own. The chosen client also rides as the `client=` row
     * filter, since a client path alone does not narrow an `allClients` caller's unrestricted scope (issue #668).
     *
     * [search] is the filter to apply, re-whitelisted to what the target client's listing declares -- a trait
     * filter from another client is dropped rather than 400ing the page, while the client and user scope
     * selectors (declared on every variant) are kept. [includeUsers] is passed rather than read from state so a
     * mount-time call is not at the mercy of a not-yet-landed setter (the #668 trap).
     *
     * [initialLoad] says where a failure is shown: the page-level `error` (the whole card, right for the very
     * first load, when there are no controls to keep) versus `searchError` beside the controls (right for a
     * client switch, so a failed reload does not remove the client selector and strand the caller).
     *
     * Fetched first, published after (#714 review): the catalog, the endpoints it binds, the whitelisted search
     * and the first page are staged in locals until the page has arrived, so a failed switch leaves the previous
     * surface whole -- its selector value, its columns, its rows -- under the error, rather than the new client's
     * controls over the old client's rows (where a row action would then hit the wrong client's endpoint).
     */
    fun loadForClient(
        client: String?,
        search: Map<String, String>,
        sortCol: String?,
        sortDesc: Boolean,
        includeUsers: Boolean,
        initialLoad: Boolean,
        /** How many forms to show: the first batch for a client switch, what the address asks for on arrival. */
        limit: Int = listingBatch,
    ) {
        beginListAction()
        formsScope.launch {
            try {
                val cat = SchemaCatalogApi.fetchCatalog(client = client)
                val ep = findFormsListEndpoint(cat.endpoints)
                val applied = if (ep == null) emptyMap() else search.filterKeys { it in formsSearchKeys(ep.inputSchema) }
                val page = if (ep == null) null else fetchListPage(ep, limit, applied, sortCol, sortDesc, includeUsers, withSummary = true)
                // Everything has arrived: publish the surface and its page together.
                catalog = cat
                getEndpoint = findFormGetEndpoint(cat.endpoints)
                deleteEndpoint = findFormDeleteEndpoint(cat.endpoints)
                createEndpoint = findFormCreateEndpoint(cat.endpoints)
                patchEndpoint = findFormPatchEndpoint(cat.endpoints)
                valuesEndpoint = findFormValuesEndpoint(cat.endpoints)
                listEndpoint = ep
                if (ep != null) {
                    searchDraft = applied
                    appliedSearch = applied
                    sortColumn = sortCol
                    sortDescending = sortDesc
                    shown = limit
                }
                if (page != null) {
                    rows = page.rows
                    numAvailable = page.numAvailable
                    scopeAnswer = ScopeAnswer(applied[EI.user], page.scopeUser)
                    page.workflowSummary?.let { listingWorkflows = it }
                    listingColumns = page.columns
                }
                error = null
                searchError = null
            } catch (e: Throwable) {
                if (initialLoad) error = userFacingError(e) else searchError = userFacingError(e)
            } finally {
                listLoading = false
            }
        }
    }

    useEffectOnce {
        // Only an address that still names THIS page drives the open form. A move that leaves for another page
        // (the survey editor, the raw editor) also carries `g=`; reading it here set `viewingId`, and the address-sync
        // effect below then rewrote the address back to `page=forms&g=…` -- hijacking the departure into the in-page
        // view. Found by #694's status-chip link; it also caught the Actions "Edit" link.
        onRouteChange {
            val h = routeParams()
            if (h[HP.page] == listingPage) {
                viewingId = h[HP.gedra]
                routeSearch = formsSearchFromRoute(h)
            }
        }
    }

    // Clear the flash after a beat, so it plays once on arrival and a later re-render (paging, a reload) does
    // not repeat it (issue #592). The timer is cleared on the next run rather than via an effect-cleanup
    // callback -- the codebase's idiom (see `useDelayedFlag`).
    useEffect(highlightRowId) {
        highlightTimer.current?.let { clearFormsTimer(it) }
        highlightTimer.current = null
        if (highlightRowId != null) {
            highlightTimer.current = setFormsTimer({ highlightRowId = null }, formsHighlightMs)
        }
    }

    useEffectOnce {
        formsScope.launch {
            // Whether the caller administers other users (issue #562) and across clients (issue #668): a failure
            // to learn them leaves the administrative controls off, which is the safe reading -- the list itself
            // still loads. One config fetch answers both.
            val homeConfig = apiResult { HomeApi.fetchConfig() }.valueOrNull()
            // An editor (issue #1432) reads every form of its client too, so is shown whose each is, and may narrow to one.
            val canManage = homeConfig?.canManageUsers == true || homeConfig?.seesClientForms == true
            seesOthersForms = canManage
            selfUserId = homeConfig?.user?.userId
            // Only once the shell has said the caller is no administrator: a config that could not be read leaves
            // Delete offered as the endpoint does, for the backend to judge, rather than hiding it from everyone.
            limitedDelete = homeConfig != null && !homeConfig.canManageUsers
            // The freshly-read local, not the state set just above: that setter has not landed yet, so the
            // clients fetch and the chosen-client restore must branch on the value in hand (issue #668).
            val seeAllClients = homeConfig?.canSeeAllClients == true
            canSeeAllClients = seeAllClients
            hasSurvey = homeConfig?.hasSurvey == true
            // The workflow column's empty-cell copy (issue #791) and the explanations (issue #1337), from the home
            // fragment file a client overlays. Copy only decorates the page, so a failure to fetch it leaves the dash
            // and no explanation rather than failing the list.
            homeConfig?.let { cfg ->
                apiResult {
                    fetchCopyWithRetry(cfg.fragment) { apiResult { HomeApi.fetchConfig().fragment }.valueOrNull() }
                }.valueOrNull()
            }?.let { homeCopy = it }
            // The clients to offer in the filter, for a cross-client caller (issue #668) -- with each one's
            // `hasSurvey` (issue #695), so the survey-status filter can follow the chosen client.
            if (seeAllClients) {
                clientChoices = apiResult { AdminApi.listClientSummaries() }.valueOr(emptyList())
            }
            // The surface to load: a client the address carries (a bookmarked or shared chosen-client listing,
            // issue #714) when the caller may see across clients, else the caller's own. The search and sort ride
            // in the address too (issues #592, #666); `loadForClient` whitelists the search to what that client's
            // listing declares and loads the first page. The freshly-read address and local, not the state set
            // above (not landed yet). For a caller who may not see across clients the `client` selector is
            // dropped from the search too (`formsInitialSearch`), not just from the surface choice: kept, it
            // would ride as a filter they have no control to clear (#714 review).
            val routeClient = routeParams()[EI.client]?.ifBlank { null }?.takeIf { seeAllClients }
            val initialSearch = formsInitialSearch(routeParams(), seeAllClients)
            val initialSortCol = routeParams()[GSORT.sort]?.ifBlank { null }
            val initialSortDesc = routeParams()[GSORT.sortDir]?.equals(GSORT.desc, ignoreCase = true) == true
            // As many forms as the address asks for (issue #1315): a way home from a form returns to the same rows.
            loadForClient(
                routeClient, initialSearch, initialSortCol, initialSortDesc, includeUsers = canManage, initialLoad = true,
                limit = shownFromRoute(routeParams()[HP.shown]),
            )
        }
    }

    // The menu chose this page again (issue #1315): an explicit navigation, so start over as the menu's bare link
    // says -- the reader's own client, no search or sort, the first batch, re-read -- where Back and Forward
    // restore what the address names. Not on the workflow's listing, which the menu does not lead to.
    val revisits = useRevisitSinceMount()
    useEffect(revisits) {
        if (revisits == 0 || workflowMode) return@useEffect
        viewingId = null
        loadForClient(null, emptyMap(), null, false, includeUsers = seesOthersForms, initialLoad = false)
    }

    // Open whatever the address named once the first load has run -- a reload in the view, or a link from the
    // create page. Gated behind the load so it does not race the initial fetch.
    useEffect(listLoading) {
        if (!listLoading && !restored) {
            viewingId = routeParams()[HP.gedra]
            // The just-saved form to flash (issue #592). Read here, before the address-write effect rewrites the
            // address from the applied search and drops it -- so a reload does not re-flash.
            val saved = routeParams()[HP.highlight]
            highlightRowId = saved
            // When the saved row is not on this freshly-loaded page the flash lands on nothing, so leave a note
            // instead (issues #669, #758): for a filtered-out save, and for a create wherever it went, since a
            // create says so -- its flag rides beside the highlight, and is as transient. The rule reads the
            // arriving address itself and is a pure function, so the whole return-then-arrive path is unit-tested.
            savedOffscreenNote = formsArrivalNote(routeParams(), appliedSearch, rows.map { it[GDF.gedraId] as? String })
            restored = true
        }
    }

    // Resolve the open form: use the loaded page's row when it holds it, otherwise fetch it by id so a deep
    // link to a form past the current page still opens. Keyed on the id and the load, not on `rows`, so paging
    // does not re-fetch a form already on screen.
    useEffect(viewingId, restored) {
        val id = viewingId
        viewMissing = false
        viewError = null
        // A confirm belongs to the form it was opened on; moving to another form (or the list) drops it. Both
        // the view's own confirm and any armed row confirm in the list -- opening a form must not leave a primed
        // "Yes" behind on the list under it (issue #417).
        confirmingDelete = false
        deleteError = null
        rowConfirmDeleteId = null
        rowDeleteError = null
        if (id == null || !restored) {
            viewRow = null
            return@useEffect
        }
        val inPage = rows.firstOrNull { it[GDF.gedraId] == id }
        if (inPage != null) {
            viewRow = inPage
            return@useEffect
        }
        val ge = getEndpoint
        if (ge == null) {
            viewRow = null
            viewMissing = true
            return@useEffect
        }
        viewRow = null
        viewLoading = true
        formsScope.launch {
            try {
                val item = SchemaCatalogApi.invoke(ge, mapOf(GDF.gedraId to id))[EP.item].toJsonMapOrEmpty()
                if (item.isEmpty()) viewMissing = true else viewRow = item
            } catch (e: Throwable) {
                // A client (4xx) failure -- absent, or out of the caller's scope -- is the same "not yours to
                // see" the list expresses by omission. A server (5xx) or network failure is a real error, and
                // must not masquerade as "this form is gone" (issue #519 review).
                if (e is ApiError && (e.status ?: 500) < 500) viewMissing = true else viewError = userFacingError(e)
            } finally {
                viewLoading = false
            }
        }
    }

    // Keep the address in step with the open form: opening one is a navigation and earns a history entry, so Back
    // returns to the list. A `g=` naming a form the page does not hold is corrected in place rather than pushed
    // onto -- the fetch still resolves it, but it is not a list row to page back to.
    useEffect(viewingId, restored, rows, appliedSearch, sortColumn, sortDescending, shown) {
        if (!restored) {
            return@useEffect
        }
        // The applied search rides in the address too (issue #592): shareable, restored on mount, and carried
        // across an edit. It is not part of `formsIdentity` (only the open form is), so a filter change replaces
        // the entry in place rather than pushing one -- typing a filter never spams Back.
        val params = buildList {
            add(HP.page to listingPage)
            viewingId?.let { add(HP.gedra to it) }
            addAll(formsSearchRouteParams(appliedSearch))
            addAll(sortRouteParams(sortColumn, sortDescending))
            addAll(shownRouteParams(shown))
        }
        val current = routeParams()[HP.gedra]
        val reachable = current == null || rows.any { it[GDF.gedraId] == current }
        applyRouteWrite(params, identity, reachable)
    }

    div {
        // The listing is a table-first page and takes the window, as the Users table does (`.card.full`); one form's
        // read-only view keeps the content width, since a form reads badly stretched that far.
        className = ClassName(if (viewingId == null) "card full" else "card wide")
        val cat = catalog
        val ep = listEndpoint
        // The workflow drilled into (issue #792) -- the page's identity in workflow mode, null on My forms.
        val drill = workflowDrillOf(appliedSearch)
        if (workflowMode) {
            // The workflow's listing heads itself with the workflow (its label, as the Workflows page shows it) and
            // a way back to Workflows, and switches state in place: the same three counts the Workflows page
            // offered, so moving from the engaged forms to the finished ones needs no trip back. Drawn above the
            // list's own states so the switch is there while a state lists nothing.
            div {
                className = ClassName("wf-header")
                backToListing(HMENU.pageWorkflows)
                // What a workflow and its states are (issue #1337), beside its name. On the listing only: an open form
                // is one of its forms, not the workflow.
                val workflowLabel = workflowDrillLabel(appliedSearch, listingWorkflows) ?: "Workflow"
                if (viewingId == null) {
                    WhatIsThis {
                        heading = workflowLabel
                        headingLevel = 1
                        headingMarkdown = true
                        text = homeCopy.explanation(HFRAG.workflowFormsNs)
                    }
                } else {
                    h1 { MarkdownInline { source = workflowLabel } }
                }
                if (ep != null && viewingId == null) {
                    div {
                        className = ClassName("wf-actions")
                        WAGG.drillStates.forEach { state ->
                            Button {
                                type = if (state == drill?.second) "primary" else "default"
                                onClick = {
                                    if (state != drill?.second) {
                                        // The state is one of the listing's search keys, so it rides the query like
                                        // the rest; the draft follows so a later Apply does not undo the switch.
                                        val next = appliedSearch + (WAGG.workflowState to state.name)
                                        searchDraft = searchDraft + (WAGG.workflowState to state.name)
                                        applySearch(ep, next)
                                    }
                                }
                                +workflowStateTitle(state)
                            }
                        }
                    }
                }
            }
            if (viewingId == null) {
                p {
                    className = ClassName("subtitle")
                    +workflowDrillNote(drill?.second)
                }
            }
        } else {
            // Named for whose forms it lists (issue #1091), as the menu entry that led here was -- with what a form, a
            // workflow and a task are beside it (issue #1337), on the listing only: an open form is one of them.
            WhatIsThis {
                heading = formsListingName(shell)
                headingLevel = 1
                text = if (viewingId == null) homeCopy.explanation(HFRAG.formsNs) else ""
            }
        }
        // The search beyond the listing's own identity (issue #792): on the workflow's listing the workflow and state
        // are not a narrowing of it, so "no forms match your search" and "nothing here at all" part on this.
        val narrowing = appliedSearch - formsDrillKeys

        when {
            listLoading -> p {
                className = ClassName("subtitle")
                +"Loading…"
            }
            error != null -> errorText(formsLoadFailureLead, error!!)
            cat == null || ep == null -> p {
                className = ClassName("subtitle")
                +"This account has no forms to list."
            }
            // A form is open (by click, link, or reload): show it, fetching by id if the page did not hold it.
            viewingId != null -> {
                val payloadType = cat.payloadType(ep)
                // The shared editor header (issue #726): the back link, the title, and the actions on one line
                // at the top -- so Edit and Delete are reachable on a long form without scrolling past it, and
                // the raw view reads like the survey view and the raw editor. The back link is the same
                // navigating "← My forms" the editors use: it drops `g`, and the route listener closes the
                // view in place, rows kept. Drawn for every sub-state so a loading or missing form still has a
                // way back; the actions appear once the form is up.
                val formUp = !viewLoading && viewError == null && viewRow != null
                formsEditorHeader(title = { +"View form" }, listing = listingPage) {
                    if (formUp) {
                        // Edit, offered only when the caller's surface carries the patch endpoint (issue #417).
                        patchEndpoint?.let {
                            Button {
                                type = "primary"
                                onClick = {
                                    viewingId?.let { id ->
                                        navigate(
                                            listOf(HP.page to pageEditForm, HP.from to listingPage, HP.gedra to id) +
                                                formsSearchRouteParams(appliedSearch) +
                                                sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown),
                                        )
                                    }
                                }
                                +"Edit"
                            }
                        }
                        // Delete, offered only when the caller's surface carries the endpoint. A two-step
                        // confirm, inline on the same line; on success the view closes and the list reloads
                        // from the top, where the now one-fewer forms are.
                        deleteEndpoint?.takeIf {
                            mayDeleteForm(
                                viewRow?.get(GDF.userId).toOptLong(), selfUserId, limitedDelete,
                                // Null when the form was read alone, which carries no state (issue #1452 review).
                                viewRow?.get(GDF.states)?.toJsonListOfMaps(),
                            )
                        }?.let { de ->
                            if (!confirmingDelete) {
                                Button {
                                    danger = true
                                    onClick = { confirmingDelete = true; deleteError = null }
                                    +"Delete form"
                                }
                            } else {
                                span {
                                    className = ClassName("subtitle")
                                    +deleteFormQuestion(viewingId, viewRow?.let { otherOwnerOf(it, selfUserId) })
                                }
                                Button {
                                    danger = true
                                    loading = deleting
                                    onClick = {
                                        val id = viewingId
                                        if (id != null) {
                                            deleting = true
                                            deleteError = null
                                            formsScope.launch {
                                                try {
                                                    SchemaCatalogApi.invoke(de, mapOf(GDF.gedraId to id))
                                                    confirmingDelete = false
                                                    viewingId = null
                                                    loadPage(ep, shown, appliedSearch, withSummary = true)
                                                } catch (e: Throwable) {
                                                    deleteError = userFacingError(e)
                                                } finally {
                                                    deleting = false
                                                }
                                            }
                                        }
                                    }
                                    +"Delete"
                                }
                                Button {
                                    onClick = { confirmingDelete = false }
                                    +"Cancel"
                                }
                            }
                        }
                    }
                }
                when {
                    viewLoading -> p {
                        className = ClassName("subtitle")
                        +"Loading…"
                    }
                    viewError != null -> errorText("Couldn't open the form.", viewError!!)
                    viewRow == null -> p {
                        className = ClassName("subtitle")
                        +(if (viewMissing) "That form is not in your list." else "Loading…")
                    }
                    else -> {
                        deleteError?.let { errorText("Couldn't delete the form.", it) }
                        renderForm(viewRow!!, entriesUnionOf(payloadType), payloadType, cat.fieldLayouts, listingColumns)
                    }
                }
            }
            // The list, but truly empty: no forms at all, and no search narrowing it (a search that matches
            // nothing is a different state, handled in the list branch so its box stays on screen to be cleared),
            // and no reload failure -- an empty page after a failed request is unknown, not empty (#562 review).
            rows.isEmpty() && narrowing.isEmpty() && searchError == null -> {
                // A create that returned to an empty page still says it happened (#758 review): without a filter
                // this branch is where such an arrival lands, and "you haven't created any forms yet" straight
                // after creating one is the create-reads-as-failed outcome the note exists to prevent.
                val arrivalNote = savedOffscreenNote
                if (arrivalNote != null) {
                    p {
                        className = ClassName("form-ok")
                        +arrivalNote
                    }
                } else {
                    p {
                        className = ClassName("subtitle")
                        // A workflow's listing with nothing in this state says so; the heading above still names
                        // the workflow and the switch still offers the other states.
                        +(if (workflowMode) "No forms are ${drill?.second?.let(::workflowStateHeading) ?: "listed"} here." else "You haven't created any forms yet.")
                    }
                }
                // Offered only when the caller's surface can create -- the same rule the edit/delete controls
                // follow, so the empty state never dangles a button that would 404. Not on a workflow's listing: a
                // new form is not in the workflow, so it would land on My forms, off this page.
                if (!workflowMode) createEndpoint?.let {
                    div {
                        className = ClassName("row")
                        Button {
                            type = "primary"
                            // Carries the sort like the main "New form" button (issue #669 review): this branch
                            // only renders when no filter is applied, so the search is empty, but a sort-only view
                            // (a bookmarked sorted URL on an account with no forms) would otherwise lose its order
                            // on the create round-trip. Kept identical to the other button so neither drifts.
                            onClick = {
                                navigate(
                                    listOf(HP.page to HMENU.pageNewForm, HP.from to listingPage) +
                                        formsSearchRouteParams(appliedSearch) +
                                        sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown),
                                )
                            }
                            +"Create a form"
                        }
                    }
                }
                // A cross-client admin can create for another user even with no forms of their own (issue #672
                // Slice 3): the on-behalf create is user-first, so it belongs in the empty state too, beside the
                // list branch's copy.
                if (canSeeAllClients && !workflowMode) {
                    div {
                        className = ClassName("row")
                        Button {
                            onClick = {
                                navigate(
                                    listOf(HP.page to pageCreateForUser, HP.from to listingPage) +
                                        formsSearchRouteParams(appliedSearch) +
                                        sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown),
                                )
                            }
                            +"Create a form for a user…"
                        }
                    }
                }
            }
            else -> {
                val union = entriesUnionOf(cat.payloadType(ep))
                // The client a cross-client caller has chosen to work in (issue #714), or null in the
                // cross-client view and for every ordinary caller.
                val chosenClient = appliedSearch[EI.client]?.ifBlank { null }
                // The list is the hub: "New form" leads it, so add sits beside the per-row edit/delete rather
                // than only in the top nav (issue #417). Present only when the surface can create -- and not
                // under a chosen client (#714 review): the create page makes the form in the caller's *own*
                // client, so under another client's listing the new row would land elsewhere and read as
                // "filtered out". Creating on another client's behalf is its own issue (#672 Slice 3); until
                // then the note under the selector says where a new form goes.
                if (chosenClient == null && !workflowMode) createEndpoint?.let {
                    div {
                        className = ClassName("row")
                        Button {
                            type = "primary"
                            // Carry the active search and sort into the create flow (issue #669), as the per-row
                            // edit link carries them: NewFormPage sends them back on success, so a create returns to
                            // the same filtered, sorted listing rather than the default one.
                            onClick = {
                                navigate(
                                    listOf(HP.page to HMENU.pageNewForm, HP.from to listingPage) +
                                        formsSearchRouteParams(appliedSearch) +
                                        sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown),
                                )
                            }
                            +"New form"
                        }
                    }
                }
                // Create a form for another user (issue #672 Slice 3), for a cross-client admin. User-first, so it
                // stands apart from "New form" (which makes one in the caller's own client) and is offered whether
                // or not a client is chosen -- the target user's client is what the on-behalf create uses. Carries
                // the listing's search and sort so the page's back link returns to the same list.
                if (canSeeAllClients && !workflowMode) {
                    div {
                        className = ClassName("row")
                        Button {
                            onClick = {
                                navigate(
                                    listOf(HP.page to pageCreateForUser, HP.from to listingPage) +
                                        formsSearchRouteParams(appliedSearch) +
                                        sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown),
                                )
                            }
                            +"Create a form for a user…"
                        }
                    }
                }
                // The saved-but-off-screen note (issues #669, #758): shown when a create or edit returned to a
                // list the saved row is not on -- filtered out, or for a create sorted or paged away -- so the
                // flash had nothing to land on. `form-ok` -- the save did happen, which is the point being made
                // -- and it clears on the next list action.
                savedOffscreenNote?.let {
                    p {
                        className = ClassName("form-ok")
                        +it
                    }
                }
                // Whose forms (issue #562): promoted above the search, and only for a caller who administers
                // other users -- an ordinary caller's list is their own, so there is nothing to choose.
                if (seesOthersForms) {
                    FormsScopeBar {
                        value = searchDraft[EI.user] ?: ""
                        applied = appliedUser
                        // Only the user the applied scope was answered with: a scope the listing refused, or has
                        // not answered yet, is not named with the one before it.
                        appliedWho = scopeUserFor(appliedUser, scopeAnswer)?.let { scopeUserLabel(it, canSeeAllClients) }
                        onChange = { v -> searchDraft = searchDraft + (EI.user to v) }
                        onApply = { applySearch(ep, searchDraft) }
                        // Drops the user from what is *applied*, and reverts the boxes to that: a pending edit in
                        // a filter box is not applied by a button about whose forms are shown (#562 review).
                        onShowEveryone = {
                            val kept = appliedSearch - EI.user
                            searchDraft = kept
                            applySearch(ep, kept)
                        }
                        // The type-ahead (issue #581): a typed fragment fetches matching users the caller may
                        // administer, and a pick confines the list to them by email -- keeping the applied
                        // filters, since the pick answers "whose", not "which".
                        fetchUsers = { term, cb ->
                            formsScope.launch {
                                cb(
                                    apiResult {
                                        AdminApi.searchUsers(UserSearchQuery(anyText = term), limit = maxUserSuggestions).users
                                            .map { UserPick(userPickLabel(it.name, it.username, it.primaryId), it.primaryId) }
                                    }.valueOr(emptyList()),
                                )
                            }
                        }
                        onPick = { email ->
                            val kept = appliedSearch + (EI.user to email)
                            searchDraft = kept
                            applySearch(ep, kept)
                        }
                    }
                }
                // Choose a client (issues #668, #714): a cross-client caller picks one client to work in. Picking
                // one re-fetches *that* client's surface (`loadForClient`), so the listing's columns, its search
                // fields and its edit form become that client's rather than the admin's own -- and the chosen
                // client also rides as the `client=` row filter, carried in the address and across pages. Clearing
                // goes back to the cross-client view (every client's rows, the admin's own columns, the Client
                // column that says which client each row is in).
                if (canSeeAllClients) {
                    div {
                        className = ClassName("row")
                        span {
                            className = ClassName("type-hint")
                            +"Client:"
                        }
                        Select {
                            value = appliedSearch[EI.client]?.ifBlank { null }
                            options = clientOptions(clientChoices)
                            allowClear = true
                            placeholder = formsAllClientsLabel
                            style = js("({ minWidth: 220 })")
                            onChange = { v ->
                                val chosen = (v as? String)?.ifBlank { null }
                                // The client set or dropped, and the `user` scope dropped with it (`formsSearchForClient`
                                // says why). Re-fetch the chosen client's surface (issue #714); `loadForClient`
                                // re-whitelists the search to the new client's declared fields and reloads from the
                                // top. A failed reload shows beside the controls, not as a whole-card error.
                                loadForClient(chosen, formsSearchForClient(appliedSearch, chosen), sortColumn, sortDescending, seesOthersForms, initialLoad = false)
                            }
                        }
                    }
                    // What the choice changed (#714 review): the selector alone reads as a row filter, but the
                    // columns and filters below are now the chosen client's -- and a new form would not be.
                    chosenClient?.let { chosen ->
                        p {
                            className = ClassName("type-hint")
                            +chosenClientNote(clientChoices.firstOrNull { it.clientId == chosen }?.name?.ifBlank { null } ?: chosen)
                        }
                    }
                }
                // The search the client's usage rules declared (issue #538, regrouped in #562): the list's own
                // input schema names the parameters, so a client with no usage rules shows no box. Search and
                // Apply promote the draft to the applied filter and reload from the top; Clear drops the term and
                // the filters but leaves whose forms are shown to the scope bar -- as *applied*, so a user typed
                // into the scope box and never applied is not applied by Clear either (#562 review).
                val groups = searchGroups(ep.inputSchema)
                // Whether the survey-status filter is on offer (issue #695): a fact about the client whose rows are
                // on screen -- the chosen client's (#714), read off its summary, else the caller's own from the
                // shell. A client not (yet) in the choices reads as offering none.
                val surveyFilterOffered = chosenClient?.let { id -> clientChoices.firstOrNull { it.clientId == id }?.hasSurvey == true } ?: hasSurvey
                // The panel is also worth drawing with no trait fields at all when the survey-status filter is
                // on offer: that one is every form's, not a trait's.
                if (groups.isNotEmpty() || surveyFilterOffered) {
                    FormsSearch {
                        this.groups = groups
                        values = searchDraft
                        applied = appliedSearch
                        showSurveyStatus = surveyFilterOffered
                        // What each column's field offers (issue #1500), for its box to suggest.
                        choices = listingColumns.filter { it.choices.isNotEmpty() }.associate { it.columnId to it.choices }
                        panelOpen = filtersOpen
                        onTogglePanel = { filtersOpen = !filtersOpen }
                        onChange = { name, value -> searchDraft = searchDraft + (name to value) }
                        onSearch = { applySearch(ep, searchDraft) }
                        onClear = {
                            // Clear drops the trait filters and the free-text term, but keeps what no filter control
                            // put there (`formsScopeKeys`): the user scope (issue #562) and the client scope (#668),
                            // each cleared through its own control, and a workflow listing's own workflow and state
                            // (#792), which are the page rather than a filter on it.
                            val kept = appliedSearch.filterKeys { it in formsScopeKeys }
                            searchDraft = kept
                            applySearch(ep, kept)
                        }
                        // A text box's value type-ahead (issue #581), only when the caller's surface can supply
                        // it. Fetches the distinct values the column takes across the caller's own documents.
                        fetchValues = valuesEndpoint?.let { ve ->
                            { column, prefix, cb ->
                                formsScope.launch {
                                    cb(
                                        apiResult {
                                            val resp = SchemaCatalogApi.invoke(
                                                ve,
                                                buildMap {
                                                    put(UF.columnId, column)
                                                    if (prefix.isNotBlank()) put(EI.q, prefix)
                                                    put(EP.limit, maxValueSuggestions)
                                                },
                                            )
                                            resp[EP.items].toJsonListOrEmpty().mapNotNull { it.toJsonMapOrEmpty()[UF.value] as? String }
                                        }.valueOr(emptyList()),
                                    )
                                }
                            }
                        }
                    }
                }
                searchError?.let { errorText(formsLoadFailureLead, it) }
                // A search that matched nothing: the box stays above so it can be changed or cleared, and a
                // plain message stands in for the count (there is nothing to show more of). After a failed reload
                // the rows are whatever was on screen before, so nothing is said about what they match.
                if (rows.isEmpty()) {
                    if (searchError == null) {
                        p {
                            className = ClassName("subtitle")
                            +"No forms match your search."
                        }
                    }
                } else {
                    p {
                        className = ClassName("type-hint")
                        +listingCountText(rows.size, numAvailable, "form", "forms")
                    }
                }
                FormsTable {
                    forms = rows.map { (it[GDF.gedraId] as? String ?: "") to summarizeForm(it, union, cat.fieldLayouts) }
                    onView = { id -> viewingId = id }
                    canDelete = deleteEndpoint != null
                    this.limitedDelete = limitedDelete
                    this.selfUserId = selfUserId
                    showOwner = seesOthersForms
                    // The Client column is for the cross-client view: it tells rows apart by client, and once a
                    // single client is chosen there is nothing to tell apart (issue #714) -- so it is dropped then.
                    showClient = canSeeAllClients && chosenClient == null
                    highlightId = highlightRowId
                    this.sortColumn = sortColumn
                    this.sortDescending = sortDescending
                    onSort = { col, desc ->
                        sortColumn = col
                        sortDescending = desc
                        shown = listingBatch
                        listEndpoint?.let { loadPage(it, listingBatch, appliedSearch, col, desc) }
                    }
                    // The listing's context (filter, sort, and how many are shown) rides every hop to a child page, so
                    // its back link returns to the same list.
                    val listingContext =
                        formsSearchRouteParams(appliedSearch) + sortRouteParams(sortColumn, sortDescending) + shownRouteParams(shown)
                    // "View Info" (issue #694): the survey's read-only view, with its Edit toggle.
                    onSurveyView = { id ->
                        navigate(listOf(HP.page to pageSurveyEdit, HP.from to listingPage, HP.gedra to id) + listingContext)
                    }
                    // The status chip's direct-to-edit link (issue #694): straight into the survey's edit mode,
                    // bypassing the read-only stop -- a real href, so it is keyboard-reachable and can open in a
                    // new tab.
                    surveyEditHref = { id ->
                        routeHref(listOf(HP.page to pageSurveyEdit, HP.from to listingPage, HP.gedra to id, HP.edit to "1") + listingContext)
                    }
                    // The workflow column (issue #791): its summary, the client's copy for an empty cell, and where
                    // a workflow link goes -- the survey page opened on that workflow, carrying the listing's context
                    // home like every other hop.
                    workflowSummary = listingWorkflows
                    columns = listingColumns
                    noWorkflowsCopy = emptyWorkflowsCopy
                    workflowHref = { id, wf, task, edit ->
                        routeHref(workflowPageRoute(id, wf, task, edit, listingPage) + listingContext)
                    }
                    // Drilled in from the workflow pages (issue #792): View Workflow and a row double-click open it.
                    drillWorkflowId = drill?.first
                    onOpenWorkflow = { id, wf, task, edit ->
                        navigate(workflowPageRoute(id, wf, task, edit, listingPage) + listingContext)
                    }
                    confirmingDeleteId = rowConfirmDeleteId
                    deletingId = rowDeletingId
                    onArmDelete = { id -> rowConfirmDeleteId = id; rowDeleteError = null }
                    onCancelDelete = { rowConfirmDeleteId = null }
                    onConfirmDelete = { id ->
                        deleteEndpoint?.let { de ->
                            rowDeletingId = id
                            rowDeleteError = null
                            formsScope.launch {
                                try {
                                    SchemaCatalogApi.invoke(de, mapOf(GDF.gedraId to id))
                                    rowConfirmDeleteId = null
                                    rowDeletingId = null
                                    // Reload as many as are shown, so the now one-fewer forms show and the rows
                                    // around the deleted one stay where they were.
                                    loadPage(ep, shown, appliedSearch, withSummary = true)
                                } catch (e: Throwable) {
                                    rowDeleteError = userFacingError(e)
                                    rowDeletingId = null
                                }
                            }
                        }
                    }
                }
                rowDeleteError?.let { errorText("Couldn't delete the form.", it) }
                // More of the same listing on request (issue #1315), under the rows it adds to.
                if (rows.isNotEmpty()) {
                    showMoreBar(rows.size, shown, numAvailable, "form", "forms", listLoading || growing) { next ->
                        shown = next
                        loadPage(ep, next, appliedSearch, grow = true)
                    }
                }
            }
        }
    }
}

/** The read-only view of one stored form: its summary, then the document itself through [SchemaForm]. */
private fun ChildrenBuilder.renderForm(
    row: Map<String, Any?>,
    union: SchType?,
    payloadType: SchType?,
    fieldLayouts: Map<String, SchLayout>,
    columns: List<ListingColumn>,
) {
    val summary = summarizeForm(row, union, fieldLayouts)
    // Read as its listing cell reads (issue #1323), against the moment the page is drawn.
    formHeading(summary, columns, DisplayZone.scriptEnv())?.let { h2 { +it } }
    if (summary.traitLabels.isNotEmpty()) {
        p {
            className = ClassName("subtitle")
            +("Contains: " + summary.traitLabels.joinToString(", "))
        }
    }
    // In full: a form's own page has the room a table does not (issue #1141).
    val created = summary.createdAt?.let { formatTimestamp(it) }
    created?.let {
        p {
            className = ClassName("subtitle")
            +"Created $it"
        }
    }
    // The last write, when it is not the creation (issue #562) -- a line that would repeat the one above says
    // nothing.
    summary.updatedAt?.let { formatTimestamp(it) }?.takeIf { it != created }?.let {
        p {
            className = ClassName("subtitle")
            +"Updated $it"
        }
    }
    p {
        className = ClassName("type-hint")
        +"Reference id"
    }
    p {
        className = ClassName("code")
        +summary.gedraId
    }
    // Read-only, friendly: the derived system fields drop away so what remains is the trait data as a
    // filled-in form. Absent only if the payload type did not resolve, in which case the summary still stands.
    payloadType?.let { type ->
        h2 { +"Details" }
        SchemaForm {
            this.type = type
            values = row
            editable = false
            friendly = true
            // Show a trait's derived data values (issue #712) -- an expense report's total, computed by the backend --
            // read-only, while the document and entry system stamps (the id, `entryId`/`source`/audit) stay
            // hidden. This form renders the whole FormDoc, so trait content is only what sits under a
            // `data` payload; `traitDataField` draws that line so the envelope is not surfaced.
            showDerivedValues = true
            traitDataField = GE.data
            // The per-type layouts (issue #586): the read-only view shows the same layout copy the edit form
            // does, cascading over the schema's title/description.
            this.fieldLayouts = fieldLayouts
        }
    }
}

